import {
  Box,
  CardProps,
  InfoIconProps,
  usePageScrollContext,
} from '@wix/design-system';
import React, {
  CSSProperties,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  AddNewButtonProps,
  CollectionToolbarProps,
  PrimaryActionButtonProps,
} from '../CollectionToolbar';
import { ComputedQuery, FiltersMap } from '@wix/bex-core';
import { CollectionSearchProps } from '../CollectionSearch';
import {
  CollectionToolbarFiltersProps,
  FilterElement,
} from '../CollectionToolbarFilters';
import { TableTopNotificationProps } from '../TableTopNotification';
import { TabsFilterProps } from '../TabsFilter';
import { ToolbarCollectionState } from '../../state';
import {
  assertComponentType,
  CollectionViewsDropdownElement,
  CustomColumnsElement,
  ReorderModeToolbarElement,
} from '../assertComponentType';
import { CollectionToolbars } from '../CollectionToolbars';
import {
  DragAndDropBulkSubmit,
  DragAndDropCancel,
  DragAndDropSubmit,
} from '../DragAndDrop';
import { TableGridSwitchButtonProps } from '../TableGridSwitchButton';
import { checkConflictingToolbarProps } from './checkConflictingToolbarProps';
import type { FiltersPanelApplyButtonBehavior } from '../FiltersPanel/FiltersPanelApplyButtonBehavior';
import type { TagsElement } from '../Tags';
import type { ExtensionMenuItemsType } from '../ExtensionMenuItems';
import { ToolbarTitleProps } from '../ToolbarTitle';

export interface ToolbarBaseProps<T> {
  /**
   * Display a title at the left of the table toolbar.
   * @external
   */
  title?: ReactNode | ReactElement<ToolbarTitleProps>;
  /**
   * Display a subtitle at the left of the table toolbar under the title.
   * * `text`: The subtitle text
   * * `info`: [InfoIconProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form--infoicon) to display an info icon next to the subtitle text
   * * `learnMore`: an object with a `url`(required) and a `label`(optional), for displaying a link button on the subtitle
   * @deprecated Use title with ToolbarTitle component instead.
   */
  subtitle?: {
    text: string;
    info?: InfoIconProps;
    learnMore?: {
      url: string;
      label?: string;
    };
  };
  /**
   * Indicates whether to show a total counter next to the table title. Please make sure to hide the counter in the page title (see hideTotal in [CollectionPage](/pages/cairo/?path=/story/components-page--collectionpage))
   * @deprecated Use title with ToolbarTitle component instead.
   */
  showTotal?: boolean;
  /**
   * Display a search input on the right of the table toolbar.<br>
   * Accepts boolean or a custom `ReactElement`. When passing `false` no search input will be displayed.
   * @default true
   * @external
   */
  search?: boolean | FilterElement;
  /**
   * Indicates whether to show Table Title Bar or not.
   * Note: if you pass `titleBarDisplay = false` title bar is not removed from the DOM. For better accessibility column titles should be defined as well.
   * @default true
   * @external
   */
  titleBarDisplay?: boolean;
  /**
   * A [`<ExportButton />`](/pages/cairo/?path=/story/components-collection-features--exportto) react element to be added to the toolbar
   */
  exportButton?: ReactElement;

  /**
   * Enables tags feature. In this element you can pass the configuration
   */
  tags?: TagsElement<T>;
  /**
   * A [`<ExportModal />`](/pages/cairo/?path=/story/components-collection-features--exportto#CTA_is_out_of_the_table) react element to be rendered in table's context
   */
  exportModal?: ReactElement;
}

export interface ToolbarCollectionBaseProps<T, F extends FiltersMap>
  extends ToolbarBaseProps<T> {
  children?: ReactNode | ReactNode[];

  /**
   * Add views dropdown to the table headers.<br>
   * Must be a [Views](/pages/cairo/?path=/story/components-collection-features--views) component.
   * @external
   */
  views?: CollectionViewsDropdownElement<F>;
  /**
   * Add filters to the table's toolbar and/or to a sliding panel.<br>
   * Must be a [CollectionToolbarFilters](/pages/cairo/?path=/story/components-collection-features--toolbar-filters) component.<br>
   * `panelTitle` prop must be passed to `<CollectionToolbarFilters />`
   * @external
   */
  filters?: ReactElement<
    CollectionToolbarFiltersProps,
    JSXElementConstructor<CollectionToolbarFiltersProps>
  >;
  /**
   * Display tabs at the left of the table toolbar.
   * @external
   */
  tabs?: ReactElement<TabsFilterProps<any>>;

  selectionToolbar?: ReactElement;

  onSearchChange?: CollectionSearchProps<T, F>['onSearchChange'];
  onSearchClick?: CollectionSearchProps<T, F>['onSearchClick'];
  onSearchBlur?: CollectionSearchProps<T, F>['onSearchBlur'];
  /**
   * The maximum length of the search input
   * @external
   */
  searchMaxLength?: CollectionSearchProps<T, F>['maxLength'];
  sticky?: boolean;
  /**
   * Minimum height for the container when showing the loader / empty state / error state
   * @default 300px
   * @external
   */
  minCardHeight?: CSSProperties['minHeight'];

  /**
   * A render function to be rendered when there's an error fetching data from the server.<br>
   * The function accepts the following parameters
   *   * `err`: The error thrown by the [fetchData](/pages/cairo/?path=/story/common-hooks--usecollection) function
   *   * `isOnline`: Indicates whether internet connection is available
   *   * `retry`: Retry collection fetch on failed response, similar to `collection.retryFetch`
   * @external
   */
  renderError?: (params: {
    err: unknown;
    isOnline: boolean;
    retry: () => void;
  }) => ReactElement<any, any>;
  /**
   * A render function to be rendered when there're no items to show in the table.<br>
   * You can use [`<CollectionEmptyState />`](/pages/cairo/?path=/story/components-collection-features--empty-state)<br>
   * The function accepts the following parameters
   *   * `hasAvailableItems`: Indicates whether other items might have been shown using other filtering / search
   *   * `query`: An instance of [ComputedQuery](/pages/cairo/?path=/story/common-models--computedquery) that represents the query that resulted with an empty state
   * @external
   */
  renderEmptyState?: (params: {
    hasAvailableItems: boolean;
    query: ComputedQuery<F>;
  }) => ReactElement<any, any>;

  cardProps?: Partial<CardProps>;
  onSelectedItems?: (isSelectAll: boolean, items: T[]) => unknown;
  /**
   * Renders a [`<TableTopNotification />`](/pages/cairo/?path=/story/components-collection-features--table-top-notification) below the table header
   * @external
   */
  topNotification?:
    | ReactElement<TableTopNotificationProps>
    | ((params: {
        query: ComputedQuery<F>;
      }) =>
        | ReactElement<TableTopNotificationProps>
        | boolean
        | null
        | undefined)
    | boolean
    | null
    | undefined;

  extraToolbar?: ReactNode;

  /**
   * Display summary bar below the toolbar.
   * @external
   */
  summaryBar?: ReactNode;
  tableGridSwitchButton?: ReactElement<TableGridSwitchButtonProps>;

  /**
   * A function that submits a drop event to the server. The function accepts the following arguments: <br>
   * - `from: {item: T; index: number}` - Item that was dragged
   * - `after: null | {item: T; index: number}` - Item that the dragged item was dropped after. If dropped at the head of the collection, this argument will be `null`
   * - `filters` - Currently applied filters, for cases your server holds a unique ordering per filtering (see `dragAndDropCategories` prop)
   *
   * @returns Promise
   * @external
   */
  dragAndDropSubmit?: DragAndDropSubmit<T, F>;

  /**
   * A function that submits a drop event to the server. The function accepts the array of following objects: <br>
   * - `from: {item: T; index: number}` - Item that was dragged
   * - `after: null | {item: T; index: number}` - Item that the dragged item was dropped after. If dropped at the head of the collection, this argument will be `null`
   * - `filters` - Currently applied filters, for cases your server holds a unique ordering per filtering (see `dragAndDropCategories` prop)
   *
   * @returns Promise
   * @external
   */
  dragAndDropBulkSubmit?: DragAndDropBulkSubmit<T, F>;

  /**
   * A function that cancels a drop event. The function accepts the following arguments: <br>
   * - `from: {item: T; index: number}` - Item that was dragged
   * - `after: null | {item: T; index: number}` - Item that the dragged item was dropped after. If dropped at the head of the collection, this argument will be `null`
   * - `filters` - Currently applied filters, for cases your server holds a unique ordering per filtering (see `dragAndDropCategories` prop)
   *
   * If the move should be cancelled, the function should return a toast config object with a message explaining why the move was cancelled. Otherwise, the function should return nullish value.
   * @external
   */
  dragAndDropCancel?: DragAndDropCancel<T, F>;

  /**
   * List of filter names combinations that your server holds a unique ordering for. If this prop is not passed, drag and drop will be enabled only when no filters are applied.<br>
   * If you pass this prop, you should explicitly pass `[]` as one of the combinations in order to enable drag and drop when no filters are applied, for example: `dragAndDropCategories={[[], ['status']]}`
   * @external
   */
  dragAndDropCategories?: (keyof F)[][] | null;

  /**
   * Extension Menu Items implementation: `import { ExtensionMenuItems } from '@wix/cairo';`
   * @external
   */
  extensionMenuItems?: ExtensionMenuItemsType;

  /**
   * Drag and drop reorder mode toolbar. Must be a ReorderModeToolbar component.
   * Accepts the following props:
   * - `learnMoreUrl`
   * - `reorderModeTitle`
   * @external
   */
  dragAndDropReorderModeToolbar?: ReorderModeToolbarElement | null;

  /**
   * A [`<CustomColumns />`](/pages/cairo/?path=/story/components-collection-features--custom-columns) react element to be added at the top right of the table.
   * @external
   */
  customColumns?: CustomColumnsElement | null;

  /**
   * Multi level sorting.
   * Enables sorting on multiple columns simultaneously.
   * @external
   */
  multiLevelSorting?: CollectionToolbarProps<T, F>['multiLevelSorting'];

  /**
   * @deprecated Use primaryActionButton instead.
   * Add New button in the toolbar.
   * Must be a AddNewButton component.
   */
  addNewButton?: ReactElement<AddNewButtonProps>;
  /**
   * Primary action button in the toolbar.
   * Must be a PrimaryActionButton component.
   * @external
   */
  primaryActionButton?: ReactElement<PrimaryActionButtonProps>;

  /**
   * Add `Apply` button to the filters panel footer. Panel filters will be applied only after the button is clicked.
   * @external
   */
  FiltersPanelApplyButtonBehavior?: typeof FiltersPanelApplyButtonBehavior;

  /**
   * Limit the amount of rows to user can select
   * @external
   */
  maxSelection?: number;

  /**
   * Indicates whether the table should have an internal scroll (instead of the default page scroll)
   */
  internalScroll?: boolean;
}

export interface ToolbarCollectionProps<T, F extends FiltersMap>
  extends ToolbarCollectionBaseProps<T, F> {
  state: ToolbarCollectionState<T, F>;
}

export function ToolbarCollection<T, F extends FiltersMap>(
  props: ToolbarCollectionProps<T, F>,
) {
  const {
    state,
    filters,
    title,
    subtitle,
    tabs,
    views,
    extraToolbar,
    selectionToolbar,
    dragAndDropReorderModeToolbar,
    showTotal,
    sticky: stickyProp,
    search,
    onSearchChange,
    onSearchClick,
    onSearchBlur,
    searchMaxLength,
    children,
    tableGridSwitchButton,
    exportButton,
    addNewButton,
    primaryActionButton,
    summaryBar,
  } = props;
  assertComponentType(views, 'CollectionViewsDropdown', 'views');
  checkConflictingToolbarProps(props);

  const { scrollableContentRef: scrollablePageRef } = usePageScrollContext();

  const [sticky, setSticky] = useState(
    stickyProp ?? scrollablePageRef?.current != null,
  );

  useEffect(() => {
    setSticky(stickyProp ?? scrollablePageRef?.current != null);
  }, [stickyProp]);

  return (
    <>
      <CollectionToolbars
        sticky={sticky}
        state={state}
        search={search}
        views={views}
        searchMaxLength={searchMaxLength}
        onSearchChange={onSearchChange}
        onSearchClick={onSearchClick}
        onSearchBlur={onSearchBlur}
        title={title}
        subtitle={subtitle}
        tabs={tabs}
        filters={filters}
        showTotal={showTotal}
        selectionToolbar={selectionToolbar}
        dragAndDropReorderModeToolbar={dragAndDropReorderModeToolbar}
        extraToolbar={extraToolbar}
        tableGridSwitchButton={tableGridSwitchButton}
        exportButton={exportButton}
        addNewButton={addNewButton}
        primaryActionButton={primaryActionButton}
        summaryBar={summaryBar}
        titleBar={
          <Box
            width="100%"
            borderTopColor="D60"
            borderTopWidth="1px"
            borderTopStyle="solid"
            boxSizing="border-box"
          />
        }
      />
      <Box
        dataHook="toolbar-collection-content"
        width="100%"
        direction="vertical"
        minHeight={0}
        flex={1}
        verticalAlign="middle"
      >
        {children}
      </Box>
    </>
  );
}
